import {
  AnalyticsEventNameType,
  DataImport,
  DataSource,
  DataSourceCredentials,
  DataSourceType,
  DefaultApi,
  OauthAttemptStatus,
  PpressRenderAuthForm200Response,
  PpressRenderAuthFormRequest,
} from "@coeff/api";
import { COLORS, DataSourceTypeDisplayNames } from "@coeff/utils";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "antd";
import React, { useEffect, useRef, useState } from "react";
import JsxParser from "react-jsx-parser";
import styled from "styled-components";

import { Button } from "../../../common/Button";
import { Card, CardHeader } from "../../../common/Card";
import { ErrorMessage } from "../../../common/ErrorMessage";
import { WheelIcon } from "../../../common/Icons";
import { Loader } from "../../../common/Loader";
import { SecurityInfo } from "../../../common/SecurityInfo";
import { WarningMessage } from "../../../common/WarningMessage";
import { useApiClient, useEventManagerContext, useMessage } from "../../../globals/contexts";
import { useFilterSortListenAction, PpressContext } from "../../../ppress/context";
import { QueryCacheKey, useAuthContext } from "../../context/AuthContext";
import {
  assertIsDefined,
  getDataSourceApiErrorMessage as getDataSourceApiErrorMessageFallback,
  getOAuthAuthorizationUrl as getOAuthAuthorizationUrlFallback,
  sentryCapture as sentryCaptureFallback,
  isWarehouseConnectionError,
  isSageWhitelistError,
  AuthorizePPressParams,
  buildPPressMetadata as buildPPressMetadataFallback,
} from "../../utils";
import { DataSourceChangeConfirmationLocationState } from "../DataSourceChangeConfirmation";

type PPressFormStateWithPPressParams = {
  ppress_params: AuthorizePPressParams & Record<string, any>;
  form_jsx: string | undefined;
  is_loading: boolean;
  fully_valid: boolean | undefined;
  hide_security_footer?: boolean;
};

const { Content, Footer } = Layout;

const StyledContentBase = styled(Content)<{ $isModal?: boolean }>`
  && {
    background-color: ${COLORS.white};

    ${props =>
      props.$isModal
        ? `
          .graphicsLogo {
            margin-bottom: 12px;
          }

          .graphicsLogo + .title + div {
            margin-bottom: 0;
          }

          .coeff__card:has(.ant-btn-primary) {
            .ant-btn-primary {
              margin-bottom: 0
            }
          }
        `
        : `
          height: 100%;
          min-height: calc(100vh - 132px);
          margin-top: 64px;
    `}

    .label {
      color: ${COLORS.black85};
      font-weight: bold;
      margin-top: 10px;
      text-align: left;
    }

    .content {
      display: flex;
      align-items: center;
      flex-direction: column;
      margin: 20px auto;
    }
  }
`;

// Wrapper to avoid passing isModal to DOM
const StyledContent: React.FC<{ isModal?: boolean; children: React.ReactNode }> = ({
  isModal,
  children,
}) => <StyledContentBase $isModal={isModal}>{children}</StyledContentBase>;

const StyledFooter = styled(Footer)`
  padding: 0px;
  background: white;
`;

const ErrorMessageContainer = styled.div<{ isModal?: boolean }>(props => ({
  marginTop: props.isModal ? 16 : 0,
  padding: props.isModal ? "16px 16px 0px 16px" : 16,
}));

const CardContent = styled.div`
  padding: 16px;
`;

const CardPanel = styled.div`
  margin-left: 24px;

  button {
    margin-top: 6px;
    padding: 0;
    background: none;
    border: none;
    font: inherit;
    color: ${COLORS.coeblue4};
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }
`;

const ppressRenderAuthForm = async (
  apiClient: DefaultApi,
  ppress_render_auth_form_request: PpressRenderAuthFormRequest,
  captureError: typeof sentryCaptureFallback
): Promise<PpressRenderAuthForm200Response> => {
  try {
    const response = await apiClient.ppressRenderAuthForm({ ppress_render_auth_form_request });

    return response.data;
  } catch (error) {
    console.error(error);

    captureError({
      error,
      name: `ppressHelpers: POST /api/ppress/render_form`,
    });

    throw error;
  }
};

type Props = {
  isModal?: boolean;
  reauthWarning?: {
    title: string;
    message: string;
    permissionIntro?: string;
    permissions?: string[];
  };
  disableMessages?: boolean;
  dataSourceId: string | undefined;
  dataSourceType: DataSourceType;
  dataImport: DataImport | undefined;
  fromConnectedSource?: boolean;
  fromOnboarding?: boolean;
  isAuthCompleted: boolean;
  setIsAuthCompleted: (value: boolean) => void;
  onChangeConfirmationRequired: (props: DataSourceChangeConfirmationLocationState) => void;
  onDataSourceUpserted: (dataSource: DataSource) => void;
  isAuthInProgress: boolean;
  setIsAuthInProgress: (value: boolean) => void;
  userEmail: string | undefined;
  clientFlags?: Record<string, any>;
  event_props?: Record<string, any>;
  webappBaseUrl?: string;
  initPostDataSourceCreation?: (props: {
    dataSource: DataSource;
    onComplete: (ds: DataSource) => void;
  }) => void;
  gasServerGetPlatformAttributes?: () => any;
  showPostSalesforceAppSetup?: (props: { showNotRightNow: boolean }) => Promise<void>;
  // PPress namespace prop - required to pass the complete addon PPress namespace with all components
  PPress: typeof import("../../../ppress").default;
};

export const AuthorizePPressContent: React.FC<Props> = ({
  isModal,
  reauthWarning,
  disableMessages,
  dataSourceId,
  dataSourceType,
  dataImport,
  fromConnectedSource,
  fromOnboarding,
  onChangeConfirmationRequired,
  onDataSourceUpserted,
  isAuthCompleted,
  setIsAuthCompleted,
  isAuthInProgress,
  setIsAuthInProgress,
  initPostDataSourceCreation,
  userEmail,
  clientFlags = {},
  event_props = {},
  webappBaseUrl = "",
  gasServerGetPlatformAttributes,
  showPostSalesforceAppSetup,
  PPress,
}) => {
  const message = useMessage();

  const {
    getOAuthAuthorizationUrl: getOAuthAuthorizationUrlFromContext,
    buildPPressMetadata: buildPPressMetadataFromContext,
    getDataSourceApiErrorMessage: getDataSourceApiErrorMessageFromContext,
    initPostDataSourceCreation: initPostDataSourceCreationFromContext,
    sentryCapture: sentryCaptureFromContext,
  } = useAuthContext();

  const getOAuthAuthorizationUrl =
    getOAuthAuthorizationUrlFromContext ?? getOAuthAuthorizationUrlFallback;
  const buildPPressMetadata = buildPPressMetadataFromContext ?? buildPPressMetadataFallback;
  const getDataSourceApiErrorMessage =
    getDataSourceApiErrorMessageFromContext ?? getDataSourceApiErrorMessageFallback;
  const sentryCapture = sentryCaptureFromContext ?? sentryCaptureFallback;

  const completeNewDataSource = (thisDataSource: DataSource) => {
    const init = initPostDataSourceCreation ?? initPostDataSourceCreationFromContext;
    if (init) {
      init({
        dataSource: thisDataSource,
        onComplete: dataSource => onDataSourceUpserted(dataSource),
      });
    } else {
      onDataSourceUpserted(thisDataSource);
    }
  };

  const api = useApiClient();

  const { track } = useEventManagerContext();

  const [errorMessage, setErrorMessage] = useState<string>("");

  const [sourceError, setSourceError] = useState<string>("");

  const [authorizedDataSourceId, setAuthorizedDataSourceId] = useState<string>();

  const [authTab, setAuthTab] = useState<Window | null>();

  const [oauthAttemptId, setOauthAttemptId] = useState<string>();

  const salesforceWarningTimer = useRef<ReturnType<typeof setTimeout>>();

  const clearSalesforceWarningTimer = () => {
    if (salesforceWarningTimer.current) {
      clearTimeout(salesforceWarningTimer.current);
      salesforceWarningTimer.current = undefined;
    }
  };

  useEffect(() => clearSalesforceWarningTimer, []);

  const { data: authorizedDataSource } = useQuery(
    [QueryCacheKey.DATA_SOURCE, authorizedDataSourceId],
    () =>
      api
        .getDataSource({ data_source_id: authorizedDataSourceId! })
        .then(response => response.data.data_source),
    {
      enabled: isAuthCompleted && Boolean(authorizedDataSourceId),
      onError: error => {
        console.error("Error fetching authorized data source", error);
        sentryCapture({ error });
      },
    }
  );

  const { data: dataSource } = useQuery(
    [QueryCacheKey.DATA_SOURCE, dataSourceId],
    () =>
      api
        .getDataSource({ data_source_id: dataSourceId! })
        .then(response => response.data.data_source),
    {
      enabled: Boolean(dataSourceId),
      onError: error => {
        console.error("Error fetching data source", error);
        sentryCapture({ error });
      },
    }
  );

  const { data: oauthAttemptResponse } = useQuery(
    [QueryCacheKey.OAUTH_ATTEMPT, oauthAttemptId],
    () => api.getOauthAttempt({ oauth_attempt_id: oauthAttemptId! }),
    {
      enabled: Boolean(isAuthInProgress && oauthAttemptId),
      refetchInterval: response =>
        response?.data.oauth_attempt.status !== OauthAttemptStatus.InProgress ? false : 2_000,
    }
  );

  useEffect(() => {
    if (oauthAttemptResponse?.data?.oauth_attempt.status === "success") {
      clearSalesforceWarningTimer();
      setOauthAttemptId(undefined);
      setAuthorizedDataSourceId(oauthAttemptResponse?.data.oauth_attempt.data_source_id);
      setIsAuthCompleted(true);
      setIsAuthInProgress(false);

      updatePPressParams({
        error_message: "",
      });

      if (oauthAttemptResponse?.data.oauth_attempt.need_user_consent_for_data_source_change) {
        const existingDataSourceUniqueId =
          oauthAttemptResponse?.data.oauth_attempt.existing_data_source_unique_id;
        const newDataSourceUniqueId =
          oauthAttemptResponse?.data.oauth_attempt.new_data_source_unique_id;
        const credentials = oauthAttemptResponse?.data.oauth_attempt.credentials;

        assertIsDefined(existingDataSourceUniqueId);
        assertIsDefined(newDataSourceUniqueId);
        assertIsDefined(credentials);

        // This can only happen in case of re-auth data source change scenario
        assertIsDefined(dataSourceId);
        assertIsDefined(dataSourceType);

        onChangeConfirmationRequired({
          existingDataSourceUniqueId,
          newDataSourceUniqueId,
          dataSourceType,
          credentials,
          ppressMetadata: oauthAttemptResponse?.data.oauth_attempt.ppress_metadata,
          additionalDisplayFields:
            oauthAttemptResponse?.data.oauth_attempt.additional_display_fields_on_re_auth,
          dataSourceId,
          dataImport: dataImport,
        });
      }
    } else if (oauthAttemptResponse?.data.oauth_attempt.status === "failure") {
      clearSalesforceWarningTimer();
      setOauthAttemptId(undefined);
      const error_message = oauthAttemptResponse?.data.oauth_attempt.error_message ?? "";
      const sourceError = oauthAttemptResponse?.data.oauth_attempt.source_error ?? "";

      setErrorMessage(error_message);
      setSourceError(sourceError);
      setIsAuthInProgress(false);

      if (isWarehouseConnectionError(error_message)) {
        updatePPressParams({
          error_message: error_message,
        });
      }

      track("import_oauth_failed", {
        import_data_source: dataSourceType,
        data_source_id: dataSourceId,
        reason: error_message,
      });

      if (!dataSourceId) {
        track("conn_create_failed", {
          import_data_source: dataSourceType,
          data_source_id: dataSourceId,
          reason: error_message,
        });
      }
    }
  }, [oauthAttemptResponse]);

  useEffect(() => {
    if (!dataSourceId) {
      track("conn_create_viewed", {
        event_from: fromConnectedSource
          ? "add_connection"
          : fromOnboarding
          ? "app_onboarding"
          : "add_import",
        import_data_source: dataSourceType,
        data_source_id: dataSourceId,
      });
    }

    if (dataSourceType !== DataSourceType.Salesforce || dataSourceId) {
      sessionStorage.removeItem("has_back");
    }
  }, []);

  const updatePPressParams = (newParams: object) => {
    setFormState(prevState => {
      const newState = {
        ...prevState,
        ppress_params: {
          ...prevState.ppress_params,
          ...newParams,
        },
      };
      return newState;
    });
  };

  const updateFormFromServer = async (ppressParams: object = {}, validate = false) => {
    if (!dataSourceType) {
      return;
    }

    if (!validate) {
      setFormState(prevState => {
        const newState = {
          ...prevState,
          is_loading: true,
        };

        return newState;
      });
    }

    try {
      const response = await ppressRenderAuthForm(
        api,
        {
          data_source_type: dataSourceType,
          data_source_id: dataSourceId,
          ppress_params: {
            ...formState.ppress_params,
            ...ppressParams,
          },
          fully_validate: validate,
        },
        sentryCapture
      );

      if (response) {
        setFormState(prevState => ({
          ...prevState,
          is_loading: false,
          fully_valid: response.valid,
          ppress_params: {
            ...ppressParams,
            ...prevState.ppress_params,
            ...response.ppress_params,
          },
          form_jsx: response.form_jsx,
          hide_security_footer: response.hide_security_footer,
        }));
      }

      if (validate) {
        if (response.valid) {
          if (isAuthCompleted) {
            onAuthorized();
          }
        }
      }
    } catch (error) {
      setErrorMessage("Error while rendering page. Please try again");
    }
  };

  const [formState, setFormState] = useState<PPressFormStateWithPPressParams>({
    ppress_params: {},
    form_jsx: "",
    is_loading: false,
    fully_valid: false,
  });

  useEffect(() => {
    const getAuthenticationForm = async () => {
      if (!dataSourceType) {
        return;
      }

      // Set loading state to true when starting to fetch the form
      setFormState(prevState => ({
        ...prevState,
        is_loading: true,
      }));

      try {
        const { form_jsx, hide_security_footer, oauth2_authorization_url, ppress_params } =
          await ppressRenderAuthForm(
            api,
            {
              data_source_type: dataSourceType,
              data_source_id: dataSourceId,
              ppress_params: {},
              fully_validate: false,
            },
            sentryCapture
          );

        setFormState(prevState => ({
          ...prevState,
          is_loading: false,
          ppress_params: {
            ...ppress_params,
            state_token_params: {
              dataSourceType: dataSourceType || "",
              dataSourceId: dataSourceId || "",
            },
            oauth2_authorization_url,
          },
          form_jsx,
          hide_security_footer,
        }));
      } catch (_) {
        setFormState(prevState => ({
          ...prevState,
          is_loading: false,
        }));
        setErrorMessage("Error while rendering page. Please try again");
      }
    };

    getAuthenticationForm();
  }, []);

  useEffect(() => {
    if (authTab) {
      authTab.close();
    }
  }, [isAuthCompleted, errorMessage]);

  useEffect(() => {
    if (isAuthCompleted) {
      if (formState.ppress_params.postValidationNeeded) {
        // There is a post verification step for this connector, call renderForm again.
        updateFormFromServer(formState.ppress_params, true);
        return;
      }

      if (authorizedDataSourceId && !authorizedDataSource) {
        return;
      }

      onAuthorized();
    }
  }, [authorizedDataSource]);

  const { listenAddFilter, signalAddFilter, listenAddSort, signalAddSort } =
    useFilterSortListenAction();

  const onAuthorized = async (dataSource?: DataSource) => {
    const thisDataSource = dataSource ?? authorizedDataSource;

    track("import_oauth_done", {
      import_data_source: dataSourceType,
      data_source_id: dataSourceId,
    });

    if (!dataSourceId) {
      track("conn_create_done", {
        import_data_source: dataSourceType,
        data_source_id: thisDataSource?.data_source_id,
      });
    }

    if (!disableMessages && dataSourceId) {
      message.success("Connection saved");
    }

    if (dataSourceId) {
      onDataSourceUpserted(thisDataSource!);
    } else {
      if (thisDataSource?.data_source_type === DataSourceType.Salesforce) {
        let isAppInstalled = false;

        if (authorizedDataSourceId) {
          const response = await api.checkSalesforceAppInstall({
            data_source_id: authorizedDataSourceId,
          });
          isAppInstalled = response.data.is_installed;
        }

        if (isAppInstalled) {
          completeNewDataSource(thisDataSource);
          return;
        }

        if (showPostSalesforceAppSetup) {
          await showPostSalesforceAppSetup({
            showNotRightNow: true,
          }).then(() => completeNewDataSource(thisDataSource));
          return;
        }

        completeNewDataSource(thisDataSource);
        return;
      }

      completeNewDataSource(thisDataSource!);
    }
  };

  const onAPIKeyAuthConnectClick = async (metadata: Record<string, string>) => {
    setIsAuthInProgress(true);

    try {
      // Call render auth form again to validate connection details
      const { data: checkResponse } = await api.ppressRenderAuthForm({
        ppress_render_auth_form_request: {
          data_source_type: dataSourceType,
          data_source_id: dataSourceId,
          ppress_params: formState.ppress_params,
          fully_validate: true,
        },
      });

      const errorMessage = checkResponse.ppress_params?.error_message?.header;

      if (checkResponse.valid) {
        const credentials: DataSourceCredentials = {};

        if (checkResponse.client_secret_label) {
          credentials.client_secret =
            // @ts-ignore
            formState.ppress_params[checkResponse.client_secret_label] ?? "";
        }

        if (checkResponse.payload_from_keys) {
          for (const formKey of checkResponse.payload_from_keys) {
            // @ts-ignore
            credentials[formKey] = formState.ppress_params[formKey] ?? "";
          }
        }

        assertIsDefined(dataSourceType);

        const defaultDataSourceName = DataSourceTypeDisplayNames[dataSourceType ?? ""];

        const payload: { data_source: DataSource } = {
          data_source: {
            data_source_id: dataSourceId,
            data_source_type: dataSourceType,
            data_source_name: dataSource?.data_source_name ?? defaultDataSourceName,
            credentials,
            ppress_metadata: metadata,
          },
        };

        const { data: response } = dataSourceId
          ? await api.updateDataSource({
              data_source_id: dataSourceId,
              update_data_source_request: payload,
            })
          : await api.createDataSource({ create_data_source_request: payload });

        onAuthorized(response.data_source);
      } else if (isSageWhitelistError(dataSourceType, errorMessage)) {
        setSourceError(errorMessage);
        setErrorMessage(
          "Sender ID 'CoefficientMPP' needs to be authorized to make Web Services request. Refer to video guide <a href= \"https://www.youtube.com/watch?v=MoBeMjdVGiU\" target = '_blank'>here</a>."
        );
      } else if (errorMessage) {
        setErrorMessage(errorMessage);
      } else {
        setErrorMessage("Please check the connection information below and try again.");
      }
    } catch (error) {
      sentryCapture({
        error,
        name: `AuthorizePPress: POST /api/ppress/auth/render_form`,
      });

      setErrorMessage(
        getDataSourceApiErrorMessage(error) ||
          "Please check the connection information below and try again."
      );
    } finally {
      setIsAuthInProgress(false);
    }
  };

  const fetchAuthorizationParams = async ({
    email,
    dataSourceType,
  }: {
    email: string | undefined;
    dataSourceType: DataSourceType;
  }) => {
    try {
      const {
        data: { oauth_attempt },
      } = await api.createOauthAttempt({
        create_oauth_attempt_request: {
          data_source_type: dataSourceType,
          data_source_id: dataSourceId,
        },
      });

      const authorizationUrl = await getOAuthAuthorizationUrl(api, {
        email,
        oauthAttemptId: oauth_attempt.oauth_attempt_id,
        dataSourceId,
        dataSourceType,
        ppress_params: formState.ppress_params,
        ppress_metadata: buildPPressMetadata({
          dataSourceType,
          ppress_params: formState.ppress_params,
        }),
      });

      return {
        oauthAttempt: oauth_attempt,
        authorizationUrl,
      };
    } catch (error) {
      sentryCapture({
        error,
        name: `Authorize ${dataSourceType}`,
      });

      throw error;
    }
  };

  const openAuthTab = (authorizationUrl: string): boolean => {
    const newTab = window.open(authorizationUrl, "_blank");

    if (!newTab || newTab.closed || typeof newTab.closed === "undefined") {
      track(AnalyticsEventNameType.AuthorizationWindowPopupBlocked, {
        data_source: dataSourceType,
        data_source_id: dataSourceId,
      });
      setAuthTab(null);
      return false;
    }

    newTab?.focus();
    setAuthTab(newTab);
    return true;
  };

  const onAuthClick = async () => {
    setIsAuthInProgress(true);
    setSourceError("");
    setErrorMessage("");
    updatePPressParams({
      popup_blocked: false,
    });

    if (!dataSourceId) {
      track("conn_create_started", {
        import_data_source: dataSourceType,
        conn_data_source: dataSourceType,
        data_source_id: dataSourceId,
      });
    }

    track("import_oauth_started", {
      import_data_source: dataSourceType,
      conn_data_source: dataSourceType,
      data_source_id: dataSourceId,
    });

    try {
      assertIsDefined(dataSourceType);
      assertIsDefined(userEmail);

      const { authorizationUrl, oauthAttempt } = await fetchAuthorizationParams({
        email: userEmail,
        dataSourceType,
      });
      const platformAttributes = gasServerGetPlatformAttributes?.();
      const isDesktopExcel =
        typeof ADDON_PLATFORM !== "undefined" &&
        ADDON_PLATFORM === "excel" &&
        Boolean(platformAttributes?.excel_desktop_version);

      const popupOpened = openAuthTab(authorizationUrl);

      if (!popupOpened && !isDesktopExcel) {
        updatePPressParams({ popup_blocked: true });
        setIsAuthInProgress(false);
      } else {
        setOauthAttemptId(oauthAttempt?.oauth_attempt_id);

        // show warning if salesforce
        if (dataSourceType === DataSourceType.Salesforce && !dataSourceId) {
          clearSalesforceWarningTimer();
          salesforceWarningTimer.current = setTimeout(() => {
            updatePPressParams({
              show_salesforce_warning: true,
            });
          }, 2000);
        }
      }
    } catch (error) {
      message.error("Error initializing connection. Try again.");

      track("import_oauth_failed", {
        import_data_source: dataSourceType,
        data_source_id: dataSourceId,
        reason: error.message,
      });

      if (!dataSourceId) {
        track("conn_create_failed", {
          import_data_source: dataSourceType,
          data_source_id: dataSourceId,
          reason: error.message,
        });
      }
    }
  };

  const onReAuthClick = () => {
    setIsAuthCompleted(false);
    setErrorMessage("");
    setSourceError("");
    setAuthorizedDataSourceId(undefined);
    setIsAuthInProgress(true);

    onAuthClick();
  };

  const showSalesforceAppSetup = () => {
    track("sf_connection_instructions_clicked", {});
    setIsAuthInProgress(false);
    setOauthAttemptId(undefined);
    updateFormFromServer({
      show_salesforce_app_setup: true,
      show_salesforce_warning: false,
    });
  };

  useEffect(() => {
    if (errorMessage && isWarehouseConnectionError(errorMessage)) {
      updatePPressParams({
        error_message: errorMessage,
      });
    } else {
      updatePPressParams({
        error_message: "",
      });
    }
  }, [errorMessage]);

  // The error card already explains the failure, so drop the OAuth warning card below it
  useEffect(() => {
    if (errorMessage || sourceError) {
      clearSalesforceWarningTimer();
      updatePPressParams({
        show_salesforce_warning: false,
      });
    }
  }, [errorMessage, sourceError]);

  return (
    <>
      <StyledContent isModal={isModal}>
        {reauthWarning && (
          <WarningMessage
            title={reauthWarning.title}
            message={
              <>
                {reauthWarning.permissionIntro && reauthWarning.permissions?.length ? (
                  <div style={{ marginBottom: 10 }}>
                    <div>{reauthWarning.permissionIntro}</div>
                    <div style={{ margin: "8px 0", display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {reauthWarning.permissions.map(permission => (
                        <code
                          key={permission}
                          style={{
                            fontWeight: 600,
                            fontSize: 12,
                            backgroundColor: "rgba(0, 0, 0, 0.06)",
                            padding: "3px 6px",
                            borderRadius: 4,
                            overflowWrap: "anywhere",
                            maxWidth: "100%",
                          }}
                        >
                          {permission}
                        </code>
                      ))}
                    </div>
                    <div>
                      {reauthWarning.permissions.length > 1 ? "permissions." : "permission."}
                    </div>
                  </div>
                ) : null}
                <div style={{ whiteSpace: "pre-line" }}>{reauthWarning.message}</div>
              </>
            }
          />
        )}
        {(errorMessage || sourceError) && (
          <ErrorMessageContainer isModal={isModal}>
            <ErrorMessage
              screen="authorizePPress"
              title={`Unable to connect to ${DataSourceTypeDisplayNames[dataSourceType ?? ""]}`}
              message={isSageWhitelistError(dataSourceType, sourceError) ? undefined : errorMessage}
              messageHtml={
                isSageWhitelistError(dataSourceType, sourceError) ? errorMessage : undefined
              }
              style={{ margin: "0px" }}
              secondaryMessage={sourceError ? sourceError : undefined}
              dataSourceType={dataSourceType}
            />
          </ErrorMessageContainer>
        )}

        {errorMessage &&
          dataSourceType === DataSourceType.Salesforce &&
          errorMessage.includes("app has been blocked") && (
            <Card className="coeblue">
              <CardContent>
                <CardHeader icon={<WheelIcon />} title="Need help connecting?" />

                <CardPanel>
                  <button>
                    <a
                      href={
                        "https://help.coefficient.io/hc/en-us/articles/17797071598235-Salesforce#h_01H7Z4Y3A6MKZQYCS3612KWJ5G"
                      }
                      target="_blank"
                    >
                      Connecting to Salesforce guide
                    </a>
                  </button>
                </CardPanel>
              </CardContent>
            </Card>
          )}

        <PpressContext.Provider
          value={{
            ...formState,
            oauthInProgress: isAuthInProgress,
            setOauthInProgress: setIsAuthInProgress,
            triggersOnChange: () => {},
            updateFormFromServer,
            updatePPressParams,
            listenAddFilter,
            signalAddFilter,
            listenAddSort,
            signalAddSort,
            onStartWithTemplate: () => {},
            displayTemplatesPopover: false,
          }}
        >
          {formState.is_loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: isModal ? "300px" : "calc(100vh - 200px)",
                width: "100%",
              }}
            >
              <Loader size="large" />
            </div>
          ) : (
            <JsxParser
              bindings={{
                form_state: formState,
                console,
                onAuthClick: onAuthClick,
                onAuthorized: onAuthorized,
                onReAuthClick: onReAuthClick,
                onAPIKeyAuthConnectClick: onAPIKeyAuthConnectClick,
                setPPressParam: (key: string, value: unknown) => () =>
                  updatePPressParams({ [key]: value }),
                updateFormFromServer,
                showSalesforceAppSetup,
              }}
              // @ts-ignore
              components={{ PPress }}
              jsx={formState.form_jsx}
              showWarnings={true}
              disableKeyGeneration={true}
              renderInWrapper={false}
            />
          )}
        </PpressContext.Provider>
      </StyledContent>

      {!formState.hide_security_footer && !isModal ? (
        <StyledFooter>
          <SecurityInfo />
        </StyledFooter>
      ) : null}
    </>
  );
};
